import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { Play } from "lucide-react";

/**
 * Renders a kanji as an inline SVG using the KanjiVG dataset.
 *
 * Props:
 *   - ch         : the kanji character
 *   - parts      : optional array of { char, color } — colour each part's
 *                  strokes in the matching colour so the user can visually
 *                  see which strokes belong to which sub-component.
 *   - highlight  : optional char — when set, dim every other part so the
 *                  highlighted one stands out (used on hover).
 *   - size       : px size of the rendered glyph.
 *
 * The glyph renders STATIC (fully drawn) on mount. A small Play button is
 * shown next to it; clicking it replays the stroke-order animation. Each
 * stroke is drawn sequentially over ~0.3s.
 *
 * SVGs are fetched on-demand from jsDelivr's KanjiVG mirror via react-query
 * (cached indefinitely — KanjiVG strokes never change for a given character).
 */
const KVG_BASE = "https://cdn.jsdelivr.net/gh/KanjiVG/kanjivg@master/kanji";
const BASE_STROKE = "#1E1B18";
const DIM_STROKE = "#BDB6AB";
const STROKE_SECONDS = 0.3;

function codepointHex(ch) {
  return ch.codePointAt(0).toString(16).padStart(5, "0");
}

async function loadSvg(ch) {
  const resp = await fetch(`${KVG_BASE}/${codepointHex(ch)}.svg`);
  if (!resp.ok) return null;
  let text = await resp.text();
  // Strip XML prolog and DOCTYPE — these break DOMPurify's SVG profile and
  // are unnecessary for inline rendering. Sanitization itself happens in
  // the render site (see `innerHtml` useMemo) so it sits adjacent to
  // `dangerouslySetInnerHTML` for any reviewer / static-analysis tool.
  text = text.replace(/<\?xml[^?]*\?>/g, "");
  text = text.replace(/<!DOCTYPE[\s\S]*?\]>/g, "");
  text = text.replace(/<!DOCTYPE[^>]*>/g, "");
  return text.trim();
}

// For a given <path>, walk up its ancestor <g> elements and return the
// nearest part char (from `colorByChar`) it belongs to. null if none.
function partForPath(pathEl, rootEl, colorByChar) {
  let el = pathEl.parentNode;
  while (el && el !== rootEl) {
    const k = el.getAttribute && el.getAttribute("kvg:element");
    if (k && colorByChar[k]) return k;
    const orig = el.getAttribute && el.getAttribute("kvg:original");
    if (orig && colorByChar[orig]) return orig;
    el = el.parentNode;
  }
  return null;
}

function paintGlyph(rootEl, parts, highlight) {
  if (!rootEl) return;
  const numbers = rootEl.querySelector("[id^='kvg:StrokeNumbers']");
  if (numbers) numbers.style.display = "none";

  const colorByChar = Object.fromEntries((parts || []).map((p) => [p.char, p.color]));
  const paths = Array.from(rootEl.querySelectorAll("path"));

  paths.forEach((p) => {
    const part = partForPath(p, rootEl, colorByChar);
    let color = part ? colorByChar[part] : BASE_STROKE;
    let opacity = 0.95;
    if (highlight) {
      if (part === highlight) {
        opacity = 1;
      } else {
        color = DIM_STROKE;
        opacity = 0.6;
      }
    }
    p.style.transition = "stroke 0.2s, opacity 0.2s";
    p.style.stroke = color;
    p.style.opacity = opacity;
    // Ensure fully drawn (in case a previous animation left it mid-state).
    p.style.strokeDasharray = "";
    p.style.strokeDashoffset = "";
  });
}

export default function KanjiGlyph({
  ch,
  parts = null,
  highlight = null,
  size = 180,
  className = "",
}) {
  const { data: svg, isLoading } = useQuery({
    queryKey: ["kvg", ch],
    queryFn: () => loadSvg(ch),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!ch,
    retry: false,
  });
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const playTimerRef = useRef(null);
  // Sanitize the raw KanjiVG SVG with DOMPurify right next to the
  // `dangerouslySetInnerHTML` call (single source of truth — defends against
  // any future change to `loadSvg` accidentally returning untrusted markup).
  // useMemo ensures stable object identity so React doesn't re-set innerHTML
  // on every render (which would wipe the in-flight stroke-animation styles).
  const innerHtml = useMemo(
    () => ({
      __html: svg
        ? DOMPurify.sanitize(svg, {
            USE_PROFILES: { svg: true, svgFilters: true },
            ADD_ATTR: [
              "kvg:element",
              "kvg:original",
              "kvg:position",
              "kvg:radical",
              "kvg:type",
            ],
          })
        : "",
    }),
    [svg],
  );

  // Initial paint (synchronous, before browser paints) — fully drawn.
  useLayoutEffect(() => {
    if (!wrapRef.current || !svg) return;
    const inner = wrapRef.current.querySelector("svg");
    if (inner) {
      inner.setAttribute("width", size);
      inner.setAttribute("height", size);
      inner.style.fill = "none";
    }
    paintGlyph(wrapRef.current, parts, highlight);
  }, [svg, ch, parts, highlight, size]);

  // Cleanup any pending replay timer on unmount/char change.
  useEffect(() => () => {
    if (playTimerRef.current) {
      clearTimeout(playTimerRef.current);
      playTimerRef.current = null;
    }
  }, [ch]);

  const playAnimation = useCallback(() => {
    if (!wrapRef.current || !svg) return;
    const paths = Array.from(wrapRef.current.querySelectorAll("path"));
    if (!paths.length) return;

    // Phase 1 — hide all strokes (no transition).
    paths.forEach((p) => {
      const len = p.getTotalLength();
      p.style.strokeDasharray = `${len}`;
      p.style.strokeDashoffset = `${len}`;
      p.style.transition = "none";
    });
    // Force a reflow so the browser commits the hidden state.
    void wrapRef.current.offsetHeight;

    setPlaying(true);
    if (playTimerRef.current) clearTimeout(playTimerRef.current);

    // Phase 2 — next frames: attach sequential transitions and animate.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        paths.forEach((p, i) => {
          p.style.transition = `stroke-dashoffset ${STROKE_SECONDS}s linear ${i * STROKE_SECONDS}s`;
          p.style.strokeDashoffset = "0";
        });
      });
    });

    const totalMs = paths.length * STROKE_SECONDS * 1000 + 150;
    playTimerRef.current = setTimeout(() => {
      setPlaying(false);
      playTimerRef.current = null;
    }, totalMs);
  }, [svg]);

  if (isLoading || svg === undefined) {
    return (
      <div
        data-testid="kanji-glyph-loading"
        className={`flex items-center justify-center text-sumiSoft font-serif ${className}`}
        style={{ width: size, height: size }}
      >
        <span className="text-6xl opacity-40">{ch}</span>
      </div>
    );
  }
  if (svg === null) {
    return (
      <div
        data-testid="kanji-glyph-fallback"
        className={`flex items-center justify-center font-serif ${className}`}
        style={{ width: size, height: size }}
      >
        <span style={{ fontSize: size * 0.7, lineHeight: 1 }}>{ch}</span>
      </div>
    );
  }
  return (
    <div className={`relative inline-block ${className}`} style={{ width: size, height: size }}>
      <div
        ref={wrapRef}
        data-testid={`kanji-glyph-${ch}`}
        className="kanji-glyph inline-block"
        style={{ width: size, height: size, lineHeight: 0 }}
        // innerHtml is DOMPurify-sanitised (svg profile) above — this is
        // the documented safe-injection site for the KanjiVG markup.
        dangerouslySetInnerHTML={innerHtml}
      />
      <button
        type="button"
        onClick={playAnimation}
        disabled={playing}
        data-testid={`kanji-glyph-play-${ch}`}
        aria-label={`Replay stroke order for ${ch}`}
        title="Replay stroke order"
        className={`absolute top-1 right-1 w-7 h-7 flex items-center justify-center border bg-washi/90 backdrop-blur-sm transition-colors ${
          playing
            ? "border-borderDefault text-sumiSoft cursor-default"
            : "border-borderDefault text-sumi hover:border-shu hover:text-shu"
        }`}
      >
        <Play size={12} fill="currentColor" strokeWidth={0} />
      </button>
    </div>
  );
}
